import { forwardRef, useId } from 'react';
import { cn } from '@/lib/utils';
import { controlClasses, describedBy, Field } from './Field';

export interface DatePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Date only, or date + time (slot bookings, PT sessions). */
  mode?: 'date' | 'datetime' | 'time' | 'month';
  containerClassName?: string;
  inputSize?: 'sm' | 'md' | 'lg';
}

const SIZES = {
  sm: 'h-8 text-sm',
  md: 'h-9 text-sm',
  lg: 'h-11 text-base',
} as const;

const INPUT_TYPES = {
  date: 'date',
  datetime: 'datetime-local',
  time: 'time',
  month: 'month',
} as const;

/**
 * Wraps the native date input rather than shipping a calendar widget.
 *
 * The value is always the browser's ISO-ish format (`yyyy-MM-dd`) which is what
 * the API expects, the mobile OS gives a far better picker than anything we would
 * build, and `color-scheme` in index.css makes the native control follow the theme.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  { label, hint, error, mode = 'date', className, containerClassName, inputSize = 'md', id, required, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const invalid = Boolean(error);

  return (
    <Field
      label={label}
      htmlFor={inputId}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <input
        ref={ref}
        id={inputId}
        type={INPUT_TYPES[mode]}
        required={required}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(inputId, invalid, Boolean(hint))}
        className={cn(controlClasses, SIZES[inputSize], className)}
        {...props}
      />
    </Field>
  );
});

/** Two bound date inputs — the filter every report screen needs. */
export function DateRangePicker({
  from,
  to,
  onFromChange,
  onToChange,
  label,
  className,
}: {
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-end gap-2', className)}>
      <DatePicker
        label={label ? `${label} from` : 'From'}
        value={from}
        max={to || undefined}
        onChange={(e) => onFromChange(e.target.value)}
        containerClassName="flex-1"
      />
      <DatePicker
        label="To"
        value={to}
        min={from || undefined}
        onChange={(e) => onToChange(e.target.value)}
        containerClassName="flex-1"
      />
    </div>
  );
}
